// ViewDocViewer — spec decision 1: ONE viewer, two entry points. The thread
// page and the /docs page mount this exact component, so they cannot drift.
//
// The reading order is Miles's, not ours:
//
//   "we'd like all the variables of the doc displayed at the top of the page so
//    that the viewer can quickly see what the details of that doc are and then
//    look at things in their current state."
//
// So: what this document SAYS (the bespoke panel) sits on top, at full width.
// The document itself comes next — inline, in whatever state it is in, never a
// download. The activity rail sits beside it, because "what happened to it" is
// a different question from "what does it say" and shouldn't push either down.
//
// The snapshot rule (§C2) is enforced structurally: changing the selected
// version REFETCHES the detail rather than reusing the panel we already have,
// so the panel always describes the version on screen.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '@/lib/api';
import { formatWhen } from '@/lib/commandCenterClient';
import { OcEmpty, OcError, OcLoading, StatusChip } from '@/components/oc/bits';
import { ViewDocPanelView } from '@/components/viewDocs/ViewDocPanelView';
import { ViewDocVersionStrip } from '@/components/viewDocs/ViewDocVersionStrip';
import { ViewDocActivityRail } from '@/components/viewDocs/ViewDocActivityRail';
import { ViewDocPdfFrame } from '@/components/viewDocs/ViewDocPdfFrame';
import { ViewDocReplicaView } from '@/components/viewDocs/ViewDocReplicaView';
import { ViewDocRecipients } from '@/components/viewDocs/ViewDocRecipients';
import { RecordOnBehalfForm } from '@/components/viewDocs/RecordOnBehalfForm';
import {
  describeWhere,
  docStateLabel,
  docStateTone,
  mayRecordOnBehalf,
  RECORD_OWNING_SURFACE,
  recordFormForKind,
  recordUnavailableReason,
  classifyDetailFailure,
  resolveSelectedVersion,
} from '@/lib/viewDocs';
import { fetchDoc, type DocDetail } from '@/lib/viewDocsClient';

type DetailFailure = ReturnType<typeof classifyDetailFailure>;

/**
 * Classify a load failure so the viewer answers in the right voice.
 *
 * The one that matters is 403: an accommodation record refused to a viewer off
 * the RRA roster is the guard WORKING. It must read as a statement with no
 * retry button — someone hitting it is doing their job and should not come away
 * thinking the system is broken.
 */
function detailFailure(err: unknown): DetailFailure {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; message?: string } | null;
    return classifyDetailFailure(err.status, body?.message ?? body?.error ?? null);
  }
  return classifyDetailFailure(null, err instanceof Error ? err.message : null);
}

export function ViewDocViewer({
  kind,
  id,
  initialVersionId = null,
  showHeader = true,
  documentOnly = false,
}: {
  kind: string;
  id: string;
  /** Open on a specific version; falls back to the current one. */
  initialVersionId?: string | null;
  /** The thread page already states who and where — it hides the header. */
  showHeader?: boolean;
  /**
   * Render ONLY the document (PDF or form replica) and the record-on-behalf
   * form — no header, version strip, variables panel, activity or recipients.
   * Full Details owns those blocks and selects the version; without this the
   * page showed every block twice with two independent version selectors
   * (sol finding 7).
   */
  documentOnly?: boolean;
}): React.JSX.Element {
  const [detail, setDetail] = useState<DocDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [failure, setFailure] = useState<DetailFailure | null>(null);
  const [versionId, setVersionId] = useState<string | null>(initialVersionId);
  const liveRef = useRef(0);

  // A different document entirely: drop the version pin so we open on its
  // current version rather than a version id belonging to the last one.
  useEffect(() => {
    setVersionId(initialVersionId);
  }, [kind, id, initialVersionId]);

  const load = useCallback(
    async (opts: { quiet?: boolean } = {}) => {
      const token = ++liveRef.current;
      if (opts.quiet) setReloading(true);
      else setLoading(true);
      setFailure(null);
      try {
        const payload = await fetchDoc(kind, id, versionId);
        if (liveRef.current !== token) return;
        setDetail(payload);
      } catch (err) {
        if (liveRef.current !== token) return;
        setFailure(detailFailure(err));
      } finally {
        if (liveRef.current === token) {
          setLoading(false);
          setReloading(false);
        }
      }
    },
    [kind, id, versionId],
  );

  // Quiet on a version switch: the panel and document swap in place instead of
  // the whole viewer blinking back to "Loading…".
  const hasDetail = detail !== null;
  useEffect(() => {
    void load({ quiet: hasDetail });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  if (loading && !detail) return <OcLoading label="Loading the document…" />;

  if (failure && !detail) {
    // A permission boundary is not an error. No danger styling, no retry —
    // both would tell the operator something is broken when nothing is.
    if (!failure.retry) {
      return (
        <div className="alert" data-tone="info">
          <div className="alert-body">{failure.text}</div>
        </div>
      );
    }
    return (
      <OcError>
        <div>{failure.text}</div>
        <div style={{ marginTop: 'var(--space-3)' }}>
          <button
            type="button"
            className="btn"
            data-variant="secondary"
            data-size="sm"
            onClick={() => void load()}
          >
            Retry
          </button>
        </div>
      </OcError>
    );
  }

  if (!detail) return <OcEmpty>Nothing to show.</OcEmpty>;

  const versions = detail.versions ?? [];
  const selected = resolveSelectedVersion(versions, versionId ?? detail.selectedVersionId);
  const selectedId = selected?.id ?? null;
  const state = selected?.state ?? detail.state;
  const stateLabel = docStateLabel({
    state,
    stateLabel: selected?.stateLabel ?? detail.stateLabel,
  });
  const where = describeWhere(detail.propertyLabel, detail.unitLabel);
  const sentAt = selected?.sentAt ?? null;
  const recipients = detail.recipients ?? [];
  const pending = detail.pending ?? [];
  const caseHref =
    detail.caseId && detail.caseKind
      ? `/${detail.caseKind === 'owner' ? 'owners' : 'households'}/${encodeURIComponent(detail.caseId)}`
      : null;

  // The gate, stated once. `mayRecordOnBehalf` refuses every 'pdf' surface, so
  // a signable can never render a fill affordance however the payload is
  // shaped — and the API refuses it independently (403 staff_may_not_sign).
  // Past that, a form kind may still be unrecordable HERE for reasons the
  // operator deserves as a statement rather than a missing button.
  const fillable = mayRecordOnBehalf(detail);
  const recordBlocked = fillable
    ? recordUnavailableReason({ kind: detail.kind, state })
    : null;
  const recordForm = fillable && !recordBlocked ? recordFormForKind(detail.kind) : null;
  const owningSurface = RECORD_OWNING_SURFACE[detail.kind];

  // Say only what we know. With no version metadata at all, "Details" is the
  // honest heading — claiming "the current version" would assert something the
  // payload never told us.
  const panelKicker =
    selected === null
      ? 'Details'
      : selected.current
        ? 'Details — as recorded on the current version'
        : 'Details — as recorded on this earlier version';

  return (
    <div className="vd-viewer" style={reloading ? { opacity: 0.6 } : undefined}>
      {showHeader && (
        <div>
          <div className="oc-kicker">{detail.label}</div>
          <div className="vd-head">
            <h2 className="vd-title">{detail.subject}</h2>
            {caseHref && (
              <Link className="btn" data-variant="secondary" data-size="sm" to={caseHref}>
                {detail.caseKind === 'owner' ? 'Open owner file' : 'Open household'}
              </Link>
            )}
          </div>
          <div
            className="oc-meta"
            style={{
              marginTop: 'var(--space-1)',
              display: 'flex',
              alignItems: 'baseline',
              gap: 'var(--space-2)',
              flexWrap: 'wrap',
            }}
          >
            {state && <StatusChip tone={docStateTone(state)}>{stateLabel}</StatusChip>}
            {where && <span>{where}</span>}
            {sentAt && <span>sent {formatWhen(sentAt)}</span>}
          </div>
        </div>
      )}

      {failure && (
        <div className="alert" data-tone={failure.retry ? 'warning' : 'info'} role="status">
          <div className="alert-body">
            {failure.text} Showing the last version that loaded.
          </div>
        </div>
      )}

      {!documentOnly && (
        <ViewDocVersionStrip
          versions={versions}
          selectedId={selectedId}
          onSelect={(next) => setVersionId(next)}
        />
      )}

      {/* ---- The variables, on top ---- */}
      {!documentOnly && (
        <section className="oc-panel">
          <div className="oc-kicker">{panelKicker}</div>
          <ViewDocPanelView panel={detail.panel} />
        </section>
      )}

      {/* ---- The document, with the rail beside it ---- */}
      <div className="vd-body">
        <div style={{ minWidth: 0 }}>
          {detail.surface === 'pdf' ? (
            <ViewDocPdfFrame
              kind={detail.kind}
              id={detail.id}
              versionId={selectedId}
              title={detail.title ?? `${detail.label} — ${detail.subject}`}
              {...(detail.file ? { availability: detail.file } : {})}
            />
          ) : detail.replica ? (
            <>
              <div className="oc-kicker" style={{ marginBottom: 'var(--space-2)' }}>
                Their form — exactly as they see it
              </div>
              <ViewDocReplicaView replica={detail.replica} />
            </>
          ) : (
            <OcEmpty>
              This request has no form to show yet — nothing has been asked of
              them.
            </OcEmpty>
          )}

          {/* Recording on behalf belongs to the DOCUMENT: the kind already
              names the section, so there is one form here, not one per
              section. Blocked is a statement, never a hidden lock. */}
          {recordForm && (
            <RecordOnBehalfForm
              kind={detail.kind}
              id={detail.id}
              form={recordForm}
              recipients={recipients}
              onRecorded={() => void load({ quiet: true })}
            />
          )}
          {recordBlocked && (
            <div className="oc-meta" style={{ marginTop: 'var(--space-4)' }}>
              {recordBlocked}
              {owningSurface && (
                <>
                  {' '}
                  <Link to={owningSurface.href}>Open {owningSurface.label}</Link>
                </>
              )}
            </div>
          )}
        </div>

        {!documentOnly && (
        <div className="vd-side">
          <section className="oc-panel">
            <div className="oc-kicker">Activity</div>
            <ViewDocActivityRail activity={detail.activity ?? []} />
            {pending.length > 0 && (
              <>
                <div className="oc-kicker" style={{ marginTop: 'var(--space-4)' }}>
                  Still to happen
                </div>
                <ul className="vd-pending">
                  {pending.map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
                </ul>
              </>
            )}
          </section>
          <section className="oc-panel">
            <div className="oc-kicker">Sent to</div>
            <ViewDocRecipients
              kind={detail.kind}
              id={detail.id}
              recipients={recipients}
              onChanged={() => void load({ quiet: true })}
            />
          </section>
        </div>
        )}
      </div>
    </div>
  );
}
